import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { streamReader, streamUpdates } from 'lib-shared-api-utils';
import {
  ApiErrorStatusCode,
  createAuthorizationHeaderValue,
  QueryError,
} from 'lib-shared-auth';
import { config } from 'lib-shared-backend-config';

import { api } from 'app/app.api';

import {
  CreateAiAgentsApiArg,
  CreateAiAgentsApiResponse,
  CreateAiAgentsConfigCommitsApiArg,
  CreateAiAgentsDeploymentsApiArg,
  CreateAiAgentsDuplicateApiArg,
  CreateAiAgentsToolApiArg,
  CreateAiAgentsToolApiResponse,
  CreateAiAgentsToolDuplicateApiArg,
  CreateAiAgentsToolsByIdApiArg,
  CreateAiAgentsToolsByIdApiResponse,
  CreateToolsDynamicSchemaRunApiArg,
  CreateToolsDynamicSchemaRunApiResponse,
  CreateToolsIntegrationsActionsRunApiArg,
  CreateToolsIntegrationsActionsRunApiResponse,
  DeleteAiAgentsApiArg,
  DeleteAiAgentsToolApiArg,
  DeleteAiAgentsToolApiResponse,
  ReadAiAgentsApiArg,
  ReadAiAgentsApiResponse,
  ReadAiAgentsConfigCommitsApiArg,
  ReadAiAgentsConfigCommitsApiResponse,
  ReadAiAgentsDefaultsApiResponse,
  ReadAiAgentsKnowledgeBasesApiArg,
  ReadAiAgentsKnowledgeBasesApiResponse,
  ReadAiAgentsListApiArg,
  ReadAiAgentsListApiResponse,
  ReadAiAgentsToolApiArg,
  ReadAiAgentsToolApiResponse,
  ReadAiAgentsToolReferencesApiArg,
  ReadAiAgentsToolReferencesApiResponse,
  ReadAiAgentsToolsApiArg,
  ReadAiAgentsToolsApiResponse,
  ReadAiAgentsVersionsApiArg,
  ReadAiAgentsVersionsApiResponse,
  ReadAiAgentsVersionsToolsApiArg,
  ReadAiAgentsVersionsToolsApiResponse,
  ReadChatbotsQuotasV2ApiArg,
  ReadChatbotsQuotasV2ApiResponse,
  ReadToolsIntegrationsActionsApiArg,
  ReadToolsIntegrationsActionsApiResponse,
  UpdateAiAgentsApiArg,
  UpdateAiAgentsApiResponse,
  UpdateChatbotsQuotasApiArg,
  UpdateChatbotsQuotasApiResponse,
} from './types/aiAgentsApi.types';
import type { RunAiAgentRequest } from './types/runAiAgent.types';
import { createAiAgentMessageAppender } from './utils/createAiAgentMessageAppender';

export type AiAgentRunStreamResponse = {
  text: string;
  meta: {
    isLoading: boolean;
    isStreaming: boolean;
    isError: boolean;
    isSuccess: boolean;
  };
  feedback_id: string;
};

export const aiAgentsApi = api
  .enhanceEndpoints({
    addTagTypes: [
      'AiAgents',
      'AiAgent',
      'AiAgentTools',
      'AiAgentTool',
      'AiAgentConfigCommits',
      'ChatbotQuotas',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // AI Agent CRUD
      createAiAgents: builder.mutation<
        CreateAiAgentsApiResponse,
        CreateAiAgentsApiArg
      >({
        query: ({ name, organizationId, knowledgeBases, user, variant }) => ({
          version: 1,
          path: 'ai-agents',
          method: 'POST',
          body: {
            name,
            organizationId,
            knowledgeBases,
            user,
            variant,
          },
        }),
        transformResponse: (response: unknown): CreateAiAgentsApiResponse => ({
          data: response as CreateAiAgentsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgents'],
      }),
      readAiAgents: builder.query<ReadAiAgentsApiResponse, ReadAiAgentsApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}`,
        }),
        transformResponse: (response: unknown): ReadAiAgentsApiResponse => ({
          data: response as ReadAiAgentsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgent'],
      }),
      readAiAgentsList: builder.query<
        ReadAiAgentsListApiResponse,
        ReadAiAgentsListApiArg
      >({
        query: () => ({
          version: 1,
          path: 'ai-agents',
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsListApiResponse => ({
          data: response as ReadAiAgentsListApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgents'],
      }),
      readAiAgentsKnowledgeBases: builder.query<
        ReadAiAgentsKnowledgeBasesApiResponse,
        ReadAiAgentsKnowledgeBasesApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/knowledge-bases`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsKnowledgeBasesApiResponse => ({
          data: response as ReadAiAgentsKnowledgeBasesApiResponse['data'],
          meta: {},
        }),
      }),
      updateAiAgents: builder.mutation<
        UpdateAiAgentsApiResponse,
        UpdateAiAgentsApiArg
      >({
        query: ({ id, ...rest }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          method: 'PATCH',
          body: rest,
        }),
        transformResponse: (response: unknown): UpdateAiAgentsApiResponse => ({
          data: response as UpdateAiAgentsApiResponse['data'],
          meta: {},
        }),
        invalidatesTags: ['AiAgents', 'AiAgent'],
      }),
      deleteAiAgents: builder.mutation<void, DeleteAiAgentsApiArg>({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['AiAgents'],
      }),
      createAiAgentsDuplicate: builder.mutation<
        void,
        CreateAiAgentsDuplicateApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/duplicate`,
          method: 'POST',
        }),
        invalidatesTags: ['AiAgents'],
      }),
      readAiAgentsConfigCommits: builder.query<
        ReadAiAgentsConfigCommitsApiResponse,
        ReadAiAgentsConfigCommitsApiArg
      >({
        query: ({ id }) => ({
          version: 1,
          path: `ai-agents/${id}/config-commits`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsConfigCommitsApiResponse => ({
          data: response as ReadAiAgentsConfigCommitsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentConfigCommits'],
      }),
      createAiAgentsConfigCommits: builder.mutation<
        void,
        CreateAiAgentsConfigCommitsApiArg
      >({
        query: ({ id, chatbotVersionId, name }) => ({
          version: 1,
          path: `ai-agents/${id}/config-commits`,
          method: 'POST',
          body: { chatbotVersionId, name },
        }),
        invalidatesTags: ['AiAgentConfigCommits'],
      }),
      createAiAgentsDeployments: builder.mutation<
        void,
        CreateAiAgentsDeploymentsApiArg
      >({
        query: ({ id, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/deployments`,
          method: 'POST',
          body: { chatbotVersionId },
        }),
        invalidatesTags: ['AiAgentConfigCommits'],
      }),
      readAiAgentsVersions: builder.query<
        ReadAiAgentsVersionsApiResponse,
        ReadAiAgentsVersionsApiArg
      >({
        query: ({ id, chatbotVersionId }) => ({
          version: 1,
          path: `ai-agents/${id}/versions/${chatbotVersionId}`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsVersionsApiResponse => ({
          data: response as ReadAiAgentsVersionsApiResponse['data'],
          meta: {},
        }),
      }),
      readAiAgentsVersionsTools: builder.query<
        ReadAiAgentsVersionsToolsApiResponse,
        ReadAiAgentsVersionsToolsApiArg
      >({
        query: ({ id, chatbotVersionId, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/versions/${chatbotVersionId}/tools/${toolId}`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsVersionsToolsApiResponse => ({
          data: response as ReadAiAgentsVersionsToolsApiResponse['data'],
          meta: {},
        }),
      }),
      readAiAgentsTools: builder.query<
        ReadAiAgentsToolsApiResponse,
        ReadAiAgentsToolsApiArg
      >({
        query: ({ id, offset, limit, type }) => ({
          version: 1,
          path: `ai-agents/${id}/tools`,
          params: { offset, limit, type },
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolsApiResponse => ({
          data: response as ReadAiAgentsToolsApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTools'],
      }),
      readAiAgentsTool: builder.query<
        ReadAiAgentsToolApiResponse,
        ReadAiAgentsToolApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolApiResponse => ({
          data: response as ReadAiAgentsToolApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTool'],
      }),
      // Tools (all types)
      createAiAgentsTool: builder.mutation<
        CreateAiAgentsToolApiResponse,
        CreateAiAgentsToolApiArg
      >({
        query: ({ id, ...toolData }) => ({
          version: 1,
          path: `ai-agents/${id}/tools`,
          method: 'POST',
          body: toolData,
        }),
        invalidatesTags: ['AiAgentTools'],
      }),
      createAiAgentsToolsById: builder.mutation<
        CreateAiAgentsToolsByIdApiResponse,
        CreateAiAgentsToolsByIdApiArg
      >({
        query: ({ id, toolId, ...toolData }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          method: 'PUT',
          body: toolData,
        }),
        invalidatesTags: ['AiAgentTools', 'AiAgentTool'],
      }),
      deleteAiAgentsTool: builder.mutation<
        DeleteAiAgentsToolApiResponse,
        DeleteAiAgentsToolApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['AiAgentTools'],
      }),
      createAiAgentsToolDuplicate: builder.mutation<
        void,
        CreateAiAgentsToolDuplicateApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}/duplicate`,
          method: 'POST',
        }),
        invalidatesTags: ['AiAgentTools'],
      }),
      readAiAgentsToolReferences: builder.query<
        ReadAiAgentsToolReferencesApiResponse,
        ReadAiAgentsToolReferencesApiArg
      >({
        query: ({ id, toolId }) => ({
          version: 1,
          path: `ai-agents/${id}/tools/${toolId}/references`,
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsToolReferencesApiResponse => ({
          data: response as ReadAiAgentsToolReferencesApiResponse['data'],
          meta: {},
        }),
        providesTags: ['AiAgentTools'],
      }),
      readToolsIntegrationsActions: builder.query<
        ReadToolsIntegrationsActionsApiResponse,
        ReadToolsIntegrationsActionsApiArg
      >({
        query: ({ integrationType }) => ({
          version: 1,
          path: `tools/integrations/${integrationType}/actions`,
        }),
      }),
      createToolsIntegrationsActionsRun: builder.query<
        CreateToolsIntegrationsActionsRunApiResponse,
        CreateToolsIntegrationsActionsRunApiArg
      >({
        query: ({ integrationType, actionId, csid, configuredParameters }) => ({
          version: 1,
          path: `tools/integrations/${integrationType}/actions/${actionId}/run`,
          method: 'POST',
          params: !!csid ? { csid } : undefined,
          body: configuredParameters
            ? Object.entries(configuredParameters).map(([name, value]) => ({
                name,
                value: { value },
              }))
            : [],
        }),
      }),
      readAiAgentsDefaults: builder.query<
        ReadAiAgentsDefaultsApiResponse,
        void
      >({
        query: () => ({
          version: 1,
          path: 'ai-agents/defaults',
        }),
        transformResponse: (
          response: unknown,
        ): ReadAiAgentsDefaultsApiResponse => ({
          data: response as ReadAiAgentsDefaultsApiResponse['data'],
          meta: {},
        }),
      }),
      readChatbotsQuotasV2: builder.query<
        ReadChatbotsQuotasV2ApiResponse,
        ReadChatbotsQuotasV2ApiArg
      >({
        query: ({ year, month, feature }) => ({
          version: 2,
          path: 'chatbots/quotas',
          params: { year, month, feature },
        }),
        providesTags: ['ChatbotQuotas'],
      }),
      updateChatbotsQuotas: builder.mutation<
        UpdateChatbotsQuotasApiResponse,
        UpdateChatbotsQuotasApiArg
      >({
        query: ({ feature, extraLimit }) => ({
          version: 1,
          path: `chatbots/quotas/${feature}/extra-limit`,
          method: 'PUT',
          body: { extraLimit },
        }),
        invalidatesTags: ['ChatbotQuotas'],
      }),
      createToolsDynamicSchemaRun: builder.query<
        CreateToolsDynamicSchemaRunApiResponse,
        CreateToolsDynamicSchemaRunApiArg
      >({
        query: ({ resolverId, value, name }) => ({
          version: 1,
          path: `tools/dynamic-schema/${resolverId}/run`,
          method: 'POST',
          body: { value, ...(name && { name }) },
        }),
      }),
      runStreamAiAgent: builder.query<
        AiAgentRunStreamResponse,
        {
          agentId: string;
          dixaConversationId: string;
          userId: string;
        }
      >({
        queryFn: () =>
          ({
            data: {
              text: '',
              meta: {
                isLoading: true,
                isStreaming: false,
                isError: false,
                isSuccess: false,
              },
              feedback_id: '',
            },
          }) as MaybePromise<
            QueryReturnValue<AiAgentRunStreamResponse, QueryError>
          >,
        async onQueryStarted(
          { agentId, dixaConversationId, userId },
          { updateCachedData },
        ) {
          const conversationIdNum = Number(dixaConversationId);
          if (isNaN(conversationIdNum)) {
            updateCachedData((draft) => {
              draft.meta.isLoading = false;
              draft.meta.isError = true;
            });
            return;
          }

          await streamUpdates<AiAgentRunStreamResponse>(
            updateCachedData,
            {
              url: `${config.url.rest}/v1/ai-agents/${agentId}/run`,
              body: {
                userId,
                dixaConversationId: String(dixaConversationId),
                session: {
                  _type: 'dixa-conversation-id',
                  id: conversationIdNum,
                },
                waitForUserInput: false,
                addMessagesToConversation: false,
              } satisfies RunAiAgentRequest,
              headers: {
                Authorization: await createAuthorizationHeaderValue(),
                Accept: 'text/event-stream',
              },
            },
            createAiAgentMessageAppender(),
          );
        },
        keepUnusedDataFor: 0,
      }),
      /** The non-streaming counterpart to `runStreamAiAgent`: resolves once with the finished text. */
      createAiAgentDraft: builder.mutation<
        { text: string },
        { agentId: string; dixaConversationId: string; userId: string }
      >({
        queryFn: async (
          { agentId, dixaConversationId, userId },
          { signal },
        ) => {
          const conversationIdNum = Number(dixaConversationId);
          if (isNaN(conversationIdNum)) {
            return {
              error: {
                status: ApiErrorStatusCode.BadRequest,
                data: 'Invalid conversation id',
              } satisfies QueryError,
            };
          }

          const draft = { text: '' };
          const appendMessage = createAiAgentMessageAppender();
          let hasError = false;

          await streamReader({
            url: `${config.url.rest}/v1/ai-agents/${agentId}/run`,
            body: {
              userId,
              dixaConversationId: String(dixaConversationId),
              session: { _type: 'dixa-conversation-id', id: conversationIdNum },
              waitForUserInput: false,
              addMessagesToConversation: false,
            } satisfies RunAiAgentRequest,
            headers: {
              Authorization: await createAuthorizationHeaderValue(),
              Accept: 'text/event-stream',
            },
            signal,
            onChunk: (chunk) => appendMessage(draft, chunk),
            onDone: () => {},
            onError: () => {
              hasError = true;
            },
          });

          if (hasError) {
            return {
              error: {
                status: ApiErrorStatusCode.InternalServerError,
                data: 'Failed to generate draft',
              } satisfies QueryError,
            };
          }

          return { data: draft };
        },
      }),
    }),
  });
